import { z } from 'zod';

import { NRT_ITEM_QUANTITY_MAX } from '../nrt/requests';
import { isWellFormedOrderSku } from './catalogue';
import { MEMBER_ORDER_SKU_MAX_LENGTH } from './orders';

/** Upper bound on lines in one staff order; far above any real order, it only bounds the body. */
export const STAFF_ORDER_ITEMS_MAX = 20;

/**
 * Staff create-and-approve order: `POST /api/v1/internal/orders/members/{memberId}`.
 *
 * Shape only. Whether each sku is in the catalogue, and orderable under
 * `programKey`, needs the program and is the service's decision — both refuse
 * with the same code, so splitting them across schema and service would buy
 * nothing but two places to keep in step.
 */
export const staffOrderCreateRequestSchema = z
  .object({
    /** The program the order is placed under (e.g. `tobacco`); the member must be enrolled in it. */
    programKey: z.string().trim().min(1).max(64),
    items: z
      .array(
        z
          .object({
            sku: z
              .string()
              .trim()
              .min(1)
              .max(MEMBER_ORDER_SKU_MAX_LENGTH)
              .refine(isWellFormedOrderSku, { message: 'sku is not a well-formed product sku' }),
            quantity: z.number().int().min(1).max(NRT_ITEM_QUANTITY_MAX),
          })
          .strict(),
      )
      .min(1)
      .max(STAFF_ORDER_ITEMS_MAX),
    /**
     * Required: a staff action is the one retried most (a double-click, a lost
     * response), and without a key a retry ships the order twice. The same key
     * with the same body replays the original order; with a different body it
     * is refused.
     *
     * Printable ASCII only, so this code-unit length and the column's
     * character-length CHECK can never disagree: an astral character is two
     * UTF-16 units but one character, and a disagreement would be a 500.
     */
    idempotencyKey: z
      .string()
      .trim()
      .min(16)
      .max(128)
      .regex(/^[\x21-\x7E]+$/, 'idempotencyKey must be printable ASCII'),
  })
  .strict()
  .superRefine((value, ctx) => {
    const skus = value.items.map((item) => item.sku);
    if (new Set(skus).size !== skus.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: 'items must not repeat a sku',
      });
    }
  });
export type StaffOrderCreateRequest = z.infer<typeof staffOrderCreateRequestSchema>;
